import { Box, IconButton, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handleApiError } from '~/utils/helper';
import styles from './Profile.module.scss';
import { IAccount } from '~/utils/interface/account';
import accountApi from '~/apis/account';
import { BaseImage, BaseScrollbar } from '~/components/common';
import UserInfo from '~/assets/images/user-info.png';
import BaseRow from '~/components/common/base-row/BaseRow';
import { InformationFillIcon, MailSendLineIcon, MapPinLine, PhoneFillIcon } from '~/assets/icons';
import { IEquipment } from '~/utils/interface/equipment';
import DetailEquipmentDialog from '../equipment/component/detail-equipment-dialog/DetailEquipmentDialog';
import { useAppSelector } from '~/redux/hooks';

const cx = classNames.bind(styles);

const Profile = () => {
  const { t } = useTranslation('admin/profile');

  const { authUser } = useAppSelector((state) => state.auth);

  const [agents, setAgents] = useState<Array<IAccount>>([]);
  const [devices, setDevices] = useState<Array<IEquipment>>([]);
  const [distributors, setDistributors] = useState<Array<IAccount>>([]);
  const [technicians, setTechnicians] = useState<Array<IAccount>>([]);
  const [infoUser, setInfoUser] = useState<IAccount>();

  const [device, setDevice] = useState<IEquipment>();

  const [isOpenDetailEquipmentDialog, setIsOpenDetailEquipmentDialog] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      try {
        const response = await accountApi.getInfo();
        setAgents(response.data?.agents);
        setDevices(response.data?.devices);
        setDistributors(response.data?.distributors);
        setTechnicians(response.data?.technicians);
        setInfoUser(response.data?.info_user);
      } catch (error) {
        handleApiError(error);
      }
    })();
  }, []);

  const handleShowDetailEquipmentDialog = (data: IEquipment) => {
    setDevice(data);
    setIsOpenDetailEquipmentDialog(true);
  };

  const renderUserInformation = () => {
    if (authUser?.permissions?.[0] && ['admin', 'manufactor'].includes(authUser.permissions[0])) {
      return (
        <Box className='w-full'>
          <Box className='flex'>
            <Box className='mr-2'>
              <Typography fontWeight={'bold'} color={'black'}>
                {t('companyName')}
              </Typography>
              <Typography className='text-black'>
                <Box component='span' className='font-bold'>
                  {t('taxName')}:
                </Box>
                <Box component='span' className='ml-0.5'>
                  {t('taxNumber')}
                </Box>
              </Typography>
            </Box>
          </Box>
          <Box mt={3}>
            <Stack direction='row' gap={1} alignItems='center' marginBottom={'12px'}>
              <MailSendLineIcon fill='#005F73' />
              <Typography className='text-[#262424]'>{t('email')}</Typography>
            </Stack>
            <Stack direction='row' gap={1} alignItems='center' marginBottom={'12px'}>
              <PhoneFillIcon fill='#005F73' />
              <Typography className='text-[#262424]'>{t('phone')}</Typography>
            </Stack>
            <Stack direction='row' gap={1} alignItems='center'>
              <MapPinLine fill='#005F73' />
              <Typography className='text-[#262424]'>{t('address')}</Typography>
            </Stack>
          </Box>
        </Box>
      );
    }

    return (
      <Box className='w-full'>
        <Box className='flex'>
          <Box className='mr-2'>
            <Typography fontWeight={'bold'} color={'black'}>
              {infoUser?.name}
            </Typography>
          </Box>
        </Box>
        <Box mt={3}>
          {infoUser?.email && (
            <Stack direction='row' gap={1} alignItems='center' marginBottom={'12px'}>
              <MailSendLineIcon fill='#005F73' />
              <Typography className='text-[#262424]'>{infoUser?.email}</Typography>
            </Stack>
          )}
          {infoUser?.phone && (
            <Stack direction='row' gap={1} alignItems='center' marginBottom={'12px'}>
              <PhoneFillIcon fill='#005F73' />
              <Typography className='text-[#262424]'>{infoUser?.phone}</Typography>
            </Stack>
          )}
          {infoUser?.address && (
            <Stack direction='row' gap={1} alignItems='center'>
              <MapPinLine fill='#005F73' />
              <Typography className='text-[#262424]'>{infoUser?.address}</Typography>
            </Stack>
          )}
        </Box>
      </Box>
    );
  };

  const renderDeviceList = () => {
    if (!authUser.role_screens?.includes('device')) return '';

    return (
      <Box className='mb-4'>
        <BaseRow className='mb-3 gap-[15px] w-full !justify-between !items-center'>
          <Box className='font-bold text-[#005F73] text-[16px]'>{t('deviceList')}</Box>
          <Box className='flex gap-[6.5px]'>
            <Box className='rounded-full text-white h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2 ml-auto'>
              {devices?.length}
            </Box>
          </Box>
        </BaseRow>
        {devices?.length ? (
          <BaseScrollbar maxHeight='277px'>
            <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73] w-[98%] bg-white'>
              <Table>
                <TableBody className='divide-y divide-[#005F73]'>
                  {devices.map((row, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell className='border-none'>
                          <Box className='font-bold'>{row.device_code}</Box>
                          <Box>{row.address}</Box>
                        </TableCell>
                        <TableCell className='border-none text-right'>
                          <Box component='span' className='font-bold'>
                            {row.device_name}
                          </Box>
                        </TableCell>
                        <TableCell className='border-none w-[80px]'>
                          <IconButton onClick={() => handleShowDetailEquipmentDialog(row)}>
                            <InformationFillIcon fill='#D9D9D9' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </BaseScrollbar>
        ) : null}
      </Box>
    );
  };

  const renderAgentList = () => {
    if (!authUser.role_screens?.includes('agent')) return '';

    return (
      <Box className='mb-3'>
        <BaseRow className='mb-3 gap-[15px] w-full !justify-between !items-center'>
          <Box className='font-bold text-[#005F73] text-[16px]'>{t('agentList')}</Box>
          <Box className='flex gap-[6.5px]'>
            <Box className='rounded-full text-white h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2 ml-auto'>
              {agents?.length}
            </Box>
          </Box>
        </BaseRow>
        {agents?.length ? (
          <BaseScrollbar maxHeight='277px'>
            <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73] w-[98%] bg-white'>
              <Table>
                <TableBody className='divide-y divide-[#005F73]'>
                  {agents.map((row, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell className='border-none'>
                          <Box className='font-bold'>{row.name}</Box>
                          <Box>{row.username}</Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </BaseScrollbar>
        ) : null}
      </Box>
    );
  };

  const renderDistributorList = () => {
    if (!authUser.role_screens?.includes('distributor')) return '';

    return (
      <Box className='w-full'>
        <BaseRow className='mb-3 gap-[15px] w-full !justify-between !items-center'>
          <Box className='font-bold text-[#005F73] text-[16px]'>{t('distributorList')}</Box>
          <Box className='flex gap-[6.5px]'>
            <Box className='rounded-full text-white h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2 ml-auto'>
              {distributors?.length}
            </Box>
          </Box>
        </BaseRow>
        {distributors?.length ? (
          <BaseScrollbar maxHeight='277px'>
            <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73] w-[98%] bg-white'>
              <Table>
                <TableBody className='divide-y divide-[#005F73]'>
                  {agents.map((row, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell className='border-none'>
                          <Box className='font-bold'>{row.name}</Box>
                          <Box>{row.username}</Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </BaseScrollbar>
        ) : null}
      </Box>
    );
  };

  const renderTechnicians = () => {
    if (!authUser.role_screens?.includes('technician')) return '';

    return (
      <Box className='mb-3'>
        <BaseRow className='mb-3 gap-[15px] w-full !justify-between !items-center'>
          <Box className='font-bold text-[#005F73] text-[16px]'>{t('technicianList')}</Box>
          <Box className='flex gap-[6.5px]'>
            <Box className='rounded-full text-white h-[25px] flex items-center justify-center font-bold bg-[#005F73] p-2 ml-auto'>
              {technicians?.length}
            </Box>
          </Box>
        </BaseRow>
        {technicians?.length ? (
          <BaseScrollbar maxHeight='277px'>
            <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73] w-[98%] bg-white'>
              <Table>
                <TableBody className='divide-y divide-[#005F73]'>
                  {technicians.map((row, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell className='border-none'>
                          <Box className='font-bold'>{row.name}</Box>
                          <Box>{row.username}</Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </BaseScrollbar>
        ) : null}
      </Box>
    );
  };

  return (
    <Box className={cx('profile-container')}>
      <Box className='flex justify-between rounded p-14 mb-4 border bg-white'>
        <Box className='w-1/4 flex justify-center items-center'>
          <BaseImage src={UserInfo} className='lg:h-[100px]' />
          <Box className='ml-7'>
            <Typography className='mb-3' fontWeight={'bold'} color={'black'}>
              {infoUser?.username}
            </Typography>
            <Typography>{t('permission.' + authUser?.permissions?.[0])}</Typography>
          </Box>
        </Box>
        <Box className='w-1/2'>{renderUserInformation()}</Box>
      </Box>

      <BaseRow className='gap-4'>
        <Box className='w-full'>
          {renderDeviceList()}
          {['admin', 'manufactor'].includes(authUser?.permissions?.[0] as string) ? renderTechnicians() : ''}
        </Box>
        <Box className='w-full ml-6'>
          {renderAgentList()}
          {renderDistributorList()}
          {['admin', 'manufactor'].includes(authUser?.permissions?.[0] as string) ? '' : renderTechnicians()}
        </Box>
      </BaseRow>

      <DetailEquipmentDialog
        open={isOpenDetailEquipmentDialog}
        setOpen={setIsOpenDetailEquipmentDialog}
        data={device}
      />
    </Box>
  );
};

export default memo(Profile);
